import type { SowingInstruction } from "../../services/sowingInstructionsApi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoreHorizontal, Search, Pencil, Check, X, ClipboardList } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PaginationBar } from "@/components/ui/PaginationBar";
import { useEffect, useMemo, useState } from "react";
import { useUnitsOfMeasure } from "@/shared/hooks/useUnitsOfMeasure";
import {
  usePagedSowingInstructionsByStatuses,
  useUpdateSowingInstruction,
} from "../../hooks/useSowingInstructions";
import { useNotification } from "@/shared/hooks/useNotification";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { SowingRequisitionDialog } from "./SowingRequisitionDialog";
import {
  getStatusLabel as getRequisitionStatusLabel,
  RequisitionStatus,
} from "@/features/inventory/services/requisitionApi";

// Statuses where it makes sense for a sowing manager to raise a requisition.
// Issued (1) and PartiallySown (13) — i.e. approved, not yet fully sown.
const REQUISITIONABLE_STATUSES = new Set([1, 13]);

// Requisition statuses that mean "store has handed over (or is handing over) materials"
// — sowing manager can proceed to record. PartiallyIssued + Issued.
const REQUISITION_READY_STATUSES = new Set<number>([
  RequisitionStatus.PartiallyIssued,
  RequisitionStatus.Issued,
]);

// Read the row's embedded requisition summary (populated by the paged listing) and
// classify into a gate state. No fetch — fields come from SowingInstructionDto.
const evaluateRequisitionGate = (inst: SowingInstruction): {
  state: "missing" | "seed_missing" | "not_ready" | "ready";
  statusValue: number | undefined;
} => {
  const status = inst.latestRequisitionStatus;
  if (status == null) return { state: "missing", statusValue: undefined };
  if (inst.latestRequisitionMissingSeedBatch) return { state: "seed_missing", statusValue: status };
  if (REQUISITION_READY_STATUSES.has(status)) return { state: "ready", statusValue: status };
  return { state: "not_ready", statusValue: status };
};

const RequisitionStatusBadge = ({ instruction }: { instruction: SowingInstruction }) => {
  const gate = evaluateRequisitionGate(instruction);
  if (gate.state === "missing") {
    return (
      <Badge variant="destructive" className="text-[10px] font-medium">
        No requisition
      </Badge>
    );
  }
  if (gate.state === "seed_missing") {
    return (
      <Badge variant="destructive" className="text-[10px] font-medium">
        Seed batch not in requisition
      </Badge>
    );
  }
  return (
    <Badge
      variant={gate.state === "ready" ? "default" : "secondary"}
      className="text-[10px] font-medium"
    >
      Requisition: {getRequisitionStatusLabel(gate.statusValue ?? -1)}
    </Badge>
  );
};

// Action menu item for "Record Sowing" / status update — disabled when the requisition
// gate isn't satisfied so the sowing manager can't fire the dialog only to be blocked.
const StatusUpdateMenuItem = ({
  instruction,
  label,
  onClick,
}: {
  instruction: SowingInstruction;
  label: string;
  onClick: () => void;
}) => {
  const gate = evaluateRequisitionGate(instruction);
  const blocked =
    gate.state === "missing" || gate.state === "seed_missing" || gate.state === "not_ready";
  const blockedReason = (() => {
    switch (gate.state) {
      case "missing":
        return "Raise a requisition first";
      case "seed_missing":
        return "Add the seed batch to the requisition";
      case "not_ready":
        return `Awaiting requisition (${getRequisitionStatusLabel(gate.statusValue ?? -1)})`;
      default:
        return "";
    }
  })();
  return (
    <DropdownMenuItem
      onClick={onClick}
      disabled={blocked}
      onSelect={(e) => {
        if (blocked) e.preventDefault();
      }}
    >
      <div className="flex flex-col gap-0.5">
        <span>{label}</span>
        {blocked && (
          <span className="text-[10px] text-amber-700">{blockedReason}</span>
        )}
      </div>
    </DropdownMenuItem>
  );
};

interface SowingInstructionsTableProps {
  // Either pass already-fetched instructions (legacy/manual mode) OR pass
  // `serverPagedStatuses` and let the table run the paged query itself.
  instructions?: SowingInstruction[];
  serverPagedStatuses?: number | number[];
  /** Include SIs with unsown trays remaining regardless of status (for the Sowing tab). */
  serverPagedIncludeUnfinishedSowing?: boolean;
  /** Include SIs with at least one Sown/Germinating Sowing row (for the Germination tab). */
  serverPagedIncludeUngerminatedSowings?: boolean;
  statuses: { value: number; label: string }[];
  filterByStatus?: number | number[];
  onUpdateStatus?: (instruction: SowingInstruction) => void;
  onViewDetails?: (instruction: SowingInstruction) => void;
  onRevoke?: (instruction: SowingInstruction) => void;
  updateStatusLabel?: string;
  showSowingDate?: boolean;
}


const STATUS_COLORS: Record<number, string> = {
  0: "outline",
  1: "secondary",
  2: "default",
  3: "default",
  4: "default",
  5: "default",
  6: "secondary",
  7: "default",
  8: "destructive",
  9: "destructive",
  10: "outline",
  13: "secondary",
};

const isPartiallySown = (inst: SowingInstruction) =>
  inst.status === 13 ||
  (inst.quantitySown != null &&
    inst.quantitySown > 0 &&
    inst.quantitySown < inst.quantityToSow);

export const SowingInstructionsTable = ({
  instructions,
  serverPagedStatuses,
  serverPagedIncludeUnfinishedSowing = false,
  serverPagedIncludeUngerminatedSowings = false,
  statuses,
  filterByStatus,
  onUpdateStatus,
  onViewDetails,
  onRevoke,
  updateStatusLabel = "Update Status",
  showSowingDate = false,
}: SowingInstructionsTableProps) => {
  const serverPaged = serverPagedStatuses !== undefined;
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ scheduledStartDate: "", scheduledEndDate: "", notes: "", quantityToSow: "" });
  const [requisitionFor, setRequisitionFor] = useState<SowingInstruction | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Fire the paged query only in server-paged mode. The hook is disabled when
  // `serverPagedStatuses` is undefined so it doesn't make a call in legacy mode.
  const pagedQuery = usePagedSowingInstructionsByStatuses(
    serverPagedStatuses ?? 0,
    currentPage,
    pageSize,
    searchTerm,
    !serverPaged,
    serverPagedIncludeUnfinishedSowing,
    serverPagedIncludeUngerminatedSowings,
  );

  // When the parent changes the statuses (tab switch), or the user types a new
  // search, snap back to page 1 so the user lands on the first page of results.
  const statusesKey = useMemo(
    () =>
      JSON.stringify(
        Array.isArray(serverPagedStatuses)
          ? [...serverPagedStatuses].sort()
          : serverPagedStatuses,
      ),
    [serverPagedStatuses],
  );
  useEffect(() => {
    if (serverPaged) setCurrentPage(1);
  }, [serverPaged, statusesKey, searchTerm]);
  const unitsQuery = useUnitsOfMeasure();
  const updateMutation = useUpdateSowingInstruction();
  const notify = useNotification();
  const { hasPermission } = useAuthStore();
  const canCreateRequisition = hasPermission("requisitions.create");

  const getUnitName = (unit: number) => {
    return unitsQuery.data?.find((u) => u.value === unit)?.name || "";
  };

  const getStatusLabel = (status: number) => {
    return statuses.find((s) => s.value === status)?.label || "Unknown";
  };

  const formatWindow = (inst: SowingInstruction) => {
    const valid = (d?: string) => !!d && new Date(d).getFullYear() > 1;
    const start = valid(inst.scheduledStartDate) ? inst.scheduledStartDate! : inst.scheduledSowingDate;
    const end = valid(inst.scheduledEndDate) ? inst.scheduledEndDate! : start;
    if (!start) return "—";
    return `${new Date(start).toLocaleDateString()} – ${new Date(end).toLocaleDateString()}`;
  };

  // Show the most-progressed quantity for the row's current phase.
  // For partial-sown SIs (sown < target), always surface "X / Y" so the user
  // can see at a glance that more trays still need to be sown.
  const formatQuantity = (inst: SowingInstruction) => {
    const unit = getUnitName(inst.unit);
    const isPartialSown =
      inst.quantitySown != null &&
      inst.quantitySown > 0 &&
      inst.quantitySown < inst.quantityToSow;
    if (isPartialSown) {
      return `${inst.quantitySown} / ${inst.quantityToSow} ${unit}`;
    }
    if (inst.quantityReady != null && inst.quantityReady > 0) {
      return `${inst.quantityReady} ${unit}`;
    }
    if (inst.quantityGrowing != null && inst.quantityGrowing > 0) {
      return `${inst.quantityGrowing} ${unit}`;
    }
    if (inst.quantityGerminated != null && inst.quantityGerminated > 0) {
      return `${inst.quantityGerminated} ${unit}`;
    }
    return `${inst.quantityToSow} ${unit}`;
  };

  const formatLocation = (inst: SowingInstruction) => {
    const gh = inst.greenhouseName?.trim();
    const bed = inst.bedNumber?.trim();
    if (gh && bed) return `${gh} · ${bed}`;
    return gh || bed || "—";
  };

  const allowedStatuses = filterByStatus !== undefined
    ? (Array.isArray(filterByStatus) ? filterByStatus : [filterByStatus])
    : null;
  // In server-paged mode the backend handles filter/search/sort/slice and we
  // just render its page. In legacy mode we apply the existing client filtering.
  const localList = useMemo(() => {
    if (serverPaged) return [];
    return (instructions ?? [])
      .filter((inst) =>
        allowedStatuses !== null ? allowedStatuses.includes(inst.status) : true,
      )
      .filter(
        (inst) =>
          inst.variety.toLowerCase().includes(searchTerm.toLowerCase()) ||
          inst.id.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      .sort(
        (a, b) =>
          new Date(a.scheduledSowingDate).getTime() -
          new Date(b.scheduledSowingDate).getTime(),
      );
  }, [serverPaged, instructions, allowedStatuses, searchTerm]);

  const pageInstructions = serverPaged
    ? pagedQuery.data?.items ?? []
    : localList.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const totalCount = serverPaged
    ? pagedQuery.data?.totalCount ?? 0
    : localList.length;
  const totalPages = serverPaged
    ? Math.max(1, pagedQuery.data?.totalPages ?? 1)
    : Math.max(1, Math.ceil(localList.length / pageSize));

  // Local mode only: snap to page 1 when filters shrink the set below the page.
  useEffect(() => {
    if (serverPaged) return;
    if (currentPage > totalPages) setCurrentPage(1);
  }, [serverPaged, currentPage, totalPages]);
  useEffect(() => {
    if (serverPaged) return;
    setCurrentPage(1);
  }, [serverPaged, searchTerm, filterByStatus]);

  // Group the current page's instructions by scheduled sowing date.
  const groupedByDay = pageInstructions.reduce<Record<string, SowingInstruction[]>>(
    (acc, inst) => {
      const day = new Date(inst.scheduledSowingDate).toLocaleDateString(undefined, {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      if (!acc[day]) acc[day] = [];
      acc[day].push(inst);
      return acc;
    },
    {}
  );

  // Sort groups by date (earliest first)
  const sortedDays = Object.keys(groupedByDay).sort(
    (a, b) =>
      new Date(groupedByDay[a][0].scheduledSowingDate).getTime() -
      new Date(groupedByDay[b][0].scheduledSowingDate).getTime()
  );

  const startEdit = (inst: SowingInstruction) => {
    const valid = (d?: string) => !!d && new Date(d).getFullYear() > 1;
    setEditingId(inst.id);
    setEditForm({
      scheduledStartDate: valid(inst.scheduledStartDate)
        ? new Date(inst.scheduledStartDate!).toISOString().split("T")[0]
        : new Date(inst.scheduledSowingDate).toISOString().split("T")[0],
      scheduledEndDate: valid(inst.scheduledEndDate)
        ? new Date(inst.scheduledEndDate!).toISOString().split("T")[0]
        : new Date(inst.scheduledSowingDate).toISOString().split("T")[0],
      notes: inst.notes || "",
      quantityToSow: String(inst.quantityToSow ?? ""),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = (inst: SowingInstruction) => {
    const parsedQty = editForm.quantityToSow.trim() === ""
      ? undefined
      : Number(editForm.quantityToSow);
    if (parsedQty !== undefined && (!Number.isFinite(parsedQty) || parsedQty <= 0)) {
      notify.error("Quantity must be a positive number");
      return;
    }
    const quantityChanged =
      parsedQty !== undefined && parsedQty !== Number(inst.quantityToSow);

    updateMutation.mutate(
      {
        id: inst.id,
        scheduledSowingDate: editForm.scheduledStartDate
          ? new Date(editForm.scheduledStartDate).toISOString()
          : undefined,
        scheduledStartDate: editForm.scheduledStartDate
          ? new Date(editForm.scheduledStartDate).toISOString()
          : undefined,
        scheduledEndDate: editForm.scheduledEndDate
          ? new Date(editForm.scheduledEndDate).toISOString()
          : undefined,
        notes: editForm.notes || undefined,
        quantityToSow: quantityChanged ? parsedQty : undefined,
      },
      {
        onSuccess: () => {
          notify.success("Instruction updated");
          setEditingId(null);
        },
        onError: (err: any) => {
          notify.error(err.response?.data?.message || "Failed to update");
        },
      }
    );
  };

  return (
    <div className="space-y-4">
      <div className="relative w-64 ml-auto">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Search instructions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {sortedDays.map((day) => (
        <div key={day} className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground px-1">
            {day} ({groupedByDay[day].length})
          </h3>
          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Variety</TableHead>
                  <TableHead>Tray Type</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Sowing Window</TableHead>
                  {showSowingDate && <TableHead>Sowing Date</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupedByDay[day].map((inst) => (
                  <TableRow key={inst.id}>
                    <TableCell className="font-medium text-sm">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1.5">
                          {inst.variety}
                          {inst.seedSource === 2 && (
                            <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                              Customer Seeds
                            </span>
                          )}
                        </div>
                        {inst.customerName && (
                          <span className="text-xs text-muted-foreground">
                            {inst.customerName}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {inst.pricingMode === 2
                        ? "Per Seedling/Piece"
                        : inst.trayTypeName || (inst.trayType === 210 ? "210-Hole" : inst.trayType === 442 ? "442-Hole" : "—")}
                    </TableCell>
                    <TableCell className="text-sm">
                      {editingId === inst.id ? (
                        <Input
                          type="number"
                          min={1}
                          step="0.01"
                          value={editForm.quantityToSow}
                          onChange={(e) =>
                            setEditForm((f) => ({
                              ...f,
                              quantityToSow: e.target.value,
                            }))
                          }
                          className="h-8 w-24"
                        />
                      ) : (
                        formatQuantity(inst)
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatLocation(inst)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {editingId === inst.id ? (
                        <DateRangePicker
                          startDate={editForm.scheduledStartDate}
                          endDate={editForm.scheduledEndDate}
                          onChange={(range) =>
                            setEditForm((f) => ({
                              ...f,
                              scheduledStartDate: range.startDate || "",
                              scheduledEndDate: range.endDate || "",
                            }))
                          }
                        />
                      ) : (
                        formatWindow(inst)
                      )}
                    </TableCell>
                    {showSowingDate && (
                      <TableCell className="text-sm">
                        {inst.actualSowingDate
                          ? new Date(inst.actualSowingDate).toLocaleDateString()
                          : "—"}
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex flex-col gap-1 items-start">
                        <Badge
                          variant={
                            STATUS_COLORS[inst.status] as
                            | "outline"
                            | "secondary"
                            | "default"
                            | "destructive"
                          }
                        >
                          {getStatusLabel(inst.status)}
                        </Badge>
                        {isPartiallySown(inst) && inst.status !== 13 && (
                          <Badge
                            variant="outline"
                            className="text-[10px] font-medium border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300"
                          >
                            Partially Sown
                          </Badge>
                        )}
                        {(REQUISITIONABLE_STATUSES.has(inst.status) || isPartiallySown(inst)) && (
                          <RequisitionStatusBadge instruction={inst} />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {editingId === inst.id ? (
                        <div className="flex items-center gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => saveEdit(inst)}
                            disabled={updateMutation.isPending}
                          >
                            <Check className="w-4 h-4 text-green-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={cancelEdit}
                            disabled={updateMutation.isPending}
                          >
                            <X className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            {onViewDetails && (
                              <DropdownMenuItem
                                onClick={() => onViewDetails(inst)}
                              >
                                View Details
                              </DropdownMenuItem>
                            )}
                            {(inst.status === 0 || inst.status === 12) && (
                              <DropdownMenuItem onClick={() => startEdit(inst)}>
                                <Pencil className="w-3.5 h-3.5 mr-2" />
                                Edit
                              </DropdownMenuItem>
                            )}
                            {canCreateRequisition &&
                              (REQUISITIONABLE_STATUSES.has(inst.status) || isPartiallySown(inst)) && (
                                <DropdownMenuItem
                                  onClick={() => setRequisitionFor(inst)}
                                >
                                  <ClipboardList className="w-3.5 h-3.5 mr-2" />
                                  Create Requisition
                                </DropdownMenuItem>
                              )}
                            {onUpdateStatus && (
                              (REQUISITIONABLE_STATUSES.has(inst.status) || isPartiallySown(inst)) ? (
                                <StatusUpdateMenuItem
                                  instruction={inst}
                                  label={updateStatusLabel}
                                  onClick={() => onUpdateStatus(inst)}
                                />
                              ) : (
                                <DropdownMenuItem
                                  onClick={() => onUpdateStatus(inst)}
                                >
                                  {updateStatusLabel}
                                </DropdownMenuItem>
                              )
                            )}
                            {onRevoke && (
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => onRevoke(inst)}
                              >
                                Revoke
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ))}

      {serverPaged && pagedQuery.isLoading && (
        <div className="text-center py-8 text-gray-500">Loading…</div>
      )}

      {pageInstructions.length === 0
        && !(serverPaged && pagedQuery.isLoading) && (
          <div className="text-center py-8 text-gray-500">
            No instructions found
          </div>
        )}

      {totalCount > 0 && (
        <PaginationBar
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      )}

      {requisitionFor && (
        <SowingRequisitionDialog
          instruction={requisitionFor}
          open={!!requisitionFor}
          onOpenChange={(open) => {
            if (!open) setRequisitionFor(null);
          }}
        />
      )}
    </div>
  );
};